import { useEffect, useRef, useState } from 'react';
import { Divider, MenuItem, Stack, TextField } from '@mui/material';
import { container } from '@infrastructure/container';
import { AmazonProductMatchMapping } from '@domain/connection/models/ConnectionSettings';
import { AmazonMatchedJuststockProduct, AmazonSetupProductPreview, suggestAmazonTargetForSource } from '@domain/connection/models/AmazonMatchPreview';
import ImportPreview from '@features/connections/setup/ImportPreview';
import MatchFields from '@features/connections/setup/MatchFields';
import SetupSection from '@features/connections/setup/SetupSection';
import { AMAZON_PRODUCT_SOURCE_FIELDS } from '@shared/connection/amazon/constants/amazonProductFields';
import iso3166 from 'iso-3166-1';

interface ProductsStepProps {
  connectionId: string;
  countryOptions: { code: string; name: string }[];
  titleCountryCode: string;
  onTitleCountryChange: (code: string) => void;
  mapping: AmazonProductMatchMapping;
  onMappingChange: (mapping: AmazonProductMatchMapping) => void;
  targetOptions: { value: string; label: string }[];
  onLoadingChange: (loading: boolean) => void;
}

const ProductsStep = ({
  connectionId,
  countryOptions,
  titleCountryCode,
  onTitleCountryChange,
  mapping,
  onMappingChange,
  targetOptions,
  onLoadingChange,
}: ProductsStepProps) => {
  const [products, setProducts] = useState<AmazonSetupProductPreview[]>([]);
  const [matches, setMatches] = useState<Record<string, AmazonMatchedJuststockProduct>>({});
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappingReady, setMappingReady] = useState(false);
  const requestId = useRef(0);
  const skipNextFetch = useRef(false);
  const mappingReadyRef = useRef(mappingReady);
  const onMappingChangeRef = useRef(onMappingChange);
  const onLoadingChangeRef = useRef(onLoadingChange);
  mappingReadyRef.current = mappingReady;
  onMappingChangeRef.current = onMappingChange;
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    const current = ++requestId.current;
    const ready = mappingReadyRef.current;
    setLoading(true);
    onLoadingChangeRef.current(true);
    setError(null);
    setProducts([]);
    setMatches({});
    setNextPageToken(null);
    container.amazonConnectorRepository.getMatchPreview(
      connectionId,
      ready ? mapping : undefined,
      undefined,
      titleCountryCode || undefined,
    )
      .then(response => {
        if (current !== requestId.current) return;
        if (response.status === 'error') {
          setError(response.error.message ?? 'Could not load the products');
          return;
        }
        setProducts(response.data.products);
        setMatches(response.data.matches);
        setNextPageToken(response.data.nextPageToken ?? null);
        if (!ready) {
          const nextMapping = response.data.mapping;
          if (nextMapping.sourceField !== mapping.sourceField || nextMapping.targetField !== mapping.targetField) {
            skipNextFetch.current = true;
          }
          onMappingChangeRef.current(nextMapping);
          setMappingReady(true);
        }
      })
      .finally(() => {
        if (current === requestId.current) {
          setLoading(false);
          onLoadingChangeRef.current(false);
        }
      });
  }, [connectionId, mapping.sourceField, mapping.targetField, titleCountryCode]);

  const changeSource = (sourceField: string) => {
    setMappingReady(true);
    onMappingChange({
      sourceField,
      targetField: suggestAmazonTargetForSource(sourceField, mapping.targetField),
    });
  };

  const loadMore = async () => {
    if (!nextPageToken || loading) return;
    setLoading(true);
    const response = await container.amazonConnectorRepository.getMatchPreview(connectionId, mapping, nextPageToken, titleCountryCode || undefined);
    setLoading(false);
    if (response.status === 'error') {
      setError(response.error.message ?? 'Could not load the products');
      return;
    }
    setProducts(current => [...current, ...response.data.products]);
    setMatches(current => ({ ...current, ...response.data.matches }));
    setNextPageToken(response.data.nextPageToken ?? null);
  };

  return (
    <Stack gap={2}>
      <SetupSection title="Default language" description="Choose the language to import product information from.">
        <TextField select size="small" fullWidth value={titleCountryCode} onChange={event => onTitleCountryChange(event.target.value)}>
          {countryOptions.map(country => (
            <MenuItem key={country.code} value={country.code}>{iso3166.whereAlpha2(country.code)?.country}</MenuItem>
          ))}
        </TextField>
      </SetupSection>
      
      <Divider />

      <MatchFields
        marketplaceLabel="Amazon"
        description="We identify each unique product by linking an Amazon field to a JustStock product field."
        sourceFields={AMAZON_PRODUCT_SOURCE_FIELDS}
        mapping={mapping}
        targetOptions={targetOptions}
        onSourceChange={changeSource}
        onTargetChange={targetField => {
          setMappingReady(true);
          onMappingChange({ ...mapping, targetField });
        }}
      />

      <ImportPreview
        marketplaceLabel="Amazon"
        sourceFields={AMAZON_PRODUCT_SOURCE_FIELDS}
        sourceField={mapping.sourceField}
        products={products}
        matches={matches}
        loading={loading}
        error={error}
        hasMore={!!nextPageToken}
        onLoadMore={loadMore}
      />
    </Stack>
  );
};

export default ProductsStep;
